import { assertValue } from '@shared/helpers'
import { SecLoansHousingLoanTargetType } from '@shared/sec-loans'
import { YesNo } from '@ui-common/types'
import { formatNumber } from '@ui-common/utils/helpers'
import { testIsValidMonetaryValue } from '@ui-common/utils/validation'
import { FieldName } from 'react-hook-form'
import { IntlShape } from 'react-intl'
import { create, enforce, omitWhen, only, Suite, test } from 'vest'

import { WizardData } from '../../../types'
import { PriceInfoFormData as FormData } from './PriceInfoStep'
import { hasCompanyLoanShare } from './priceInfoStepUtils'

export const createFormValidator = (
  formatMessage: IntlShape['formatMessage'],
  applicationData: WizardData,
  minimumHousingLoanAmount: number,
): Suite<string, string, (data: FormData, changedFields?: FieldName<FormData>[]) => void> => {
  return create((data, changedFields) => {
    only(changedFields)

    testIsValidMonetaryValue('price', data.price, formatMessage, undefined, true, minimumHousingLoanAmount)

    omitWhen(
      applicationData.targetInfo?.housingLoanInfo?.targetTypeInfo?.targetType !==
        SecLoansHousingLoanTargetType.HousingStock,
      () => {
        testIsValidMonetaryValue(
          'netSellingPrice',
          data.netSellingPrice,
          formatMessage,
          undefined,
          true,
          minimumHousingLoanAmount,
        )

        omitWhen(!data.price, () => {
          test(
            'price',
            formatMessage(
              { id: 'sec-loans-validate-value-is-not-more-than-other-value' },
              {
                firstValue: formatMessage({
                  id: 'sec-loans-target-price-label',
                }),
                secondValue: formatMessage({ id: 'sec-loans-net-selling-price-label' }),
              },
            ),
            () => {
              const netSellingPrice = assertValue(data.netSellingPrice, 'data.netSellingPrice')
              const price = assertValue(data.price, 'data.price')
              enforce(formatNumber(netSellingPrice) >= formatNumber(price)).isTruthy()
            },
          )
        })

        omitWhen(
          !!data.netSellingPrice &&
            !!data.price &&
            !hasCompanyLoanShare(formatNumber(data.netSellingPrice), formatNumber(data.price)),
          () => {
            test('isPayingCompanyLoanShare', formatMessage({ id: 'validate-required-field-missing' }), () => {
              enforce(data.isPayingCompanyLoanShare).inside(Object.values(YesNo))
            })
          },
        )
      },
    )
  })
}
